<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const { t } = useI18n();
const store = useStore();

// GK-HUB: Removed showDeptFilter/departments/selectedDeptId — using team filter instead
const {
  data: breaks,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filters,
  filteredAgents,
} = useIspReport({
  getter: 'agentBreaks/getBreakSessions',
  action: 'agentBreaks/fetchBreakSessions',
  filters: { agentId: '', teamId: '' },
  initActions: ['agents/get'],
});

// GK-HUB: Teams list + showTeamFilter (admin/manager only)
const teams = computed(() => store.getters['teams/getTeams']);
store.dispatch('teams/get');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);
// GK-HUB: end team filter

// Helpers
const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '0s';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Summary data
const summary = computed(() => breaks.value.summary || {});
const sessions = computed(() => breaks.value.data || []);

const totalSessions = computed(() => summary.value.total_sessions || 0);
const totalDuration = computed(() =>
  formatDuration(summary.value.total_duration_seconds || 0)
);
const avgDuration = computed(() =>
  formatDuration(summary.value.avg_duration_seconds || 0)
);
const longestBreak = computed(() => {
  const list = sessions.value;
  if (!list.length) return '0s';
  const max = Math.max(...list.map(s => s.duration_seconds || 0));
  return formatDuration(max);
});

// Bar chart: breaks by reason
const getReasonColor = idx => {
  if (idx < 2) return 'teal';
  if (idx < 4) return 'blue';
  return 'warning';
};

const barChartItems = computed(() => {
  const byReason = summary.value.by_reason || {};
  const entries = Object.entries(byReason);
  const total = entries.reduce((sum, [, count]) => sum + (count || 0), 0) || 1;
  return entries.map(([name, count], i) => ({
    label: name || '—',
    value: count || 0,
    displayValue: String(count || 0),
    percentage: Math.round(((count || 0) / total) * 100),
    color: getReasonColor(i),
  }));
});

// Table
const tableColumns = [
  { key: 'agent', label: t('ISP_REPORTS.PAUSAS.COL_AGENT'), width: '25%' },
  { key: 'reason', label: t('ISP_REPORTS.PAUSAS.COL_REASON'), width: '20%' },
  {
    key: 'start',
    label: t('ISP_REPORTS.PAUSAS.COL_START'),
    width: '20%',
    type: 'mono',
  },
  {
    key: 'end',
    label: t('ISP_REPORTS.PAUSAS.COL_END'),
    width: '20%',
    type: 'mono',
  },
  {
    key: 'duration',
    label: t('ISP_REPORTS.PAUSAS.COL_DURATION'),
    align: 'right',
    type: 'mono',
    width: '15%',
  },
];

const tableRows = computed(() =>
  sessions.value.map(s => ({
    agent: s.user_name || '—',
    reason: s.break_reason_name || '—',
    start: formatDateTime(s.started_at),
    end: s.ended_at ? formatDateTime(s.ended_at) : '—',
    duration: formatDuration(s.duration_seconds || 0),
  }))
);

const onExportCsv = () => {
  const rows = sessions.value;
  exportCsv(
    rows,
    'Agente,Motivo,Início,Fim,Duração (s)\n',
    r =>
      `"${r.user_name || ''}","${r.break_reason_name || ''}","${r.started_at || ''}","${r.ended_at || ''}",${r.duration_seconds || 0}`,
    'pausas-agentes.csv'
  );
};
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.PAUSAS.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.PAUSAS.SUBTITLE') }}
      </p>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- GK-HUB: Team filter (replaces department) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          :value="filters.teamId.value"
          class="isp-select"
          @change="onFilterChange('teamId', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('agentId', e)">
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_AGENTS') }}
          </option>
          <option
            v-for="agent in filteredAgents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <button class="btn-export ml-auto" @click="onExportCsv">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading (initial only) -->
    <woot-loading-state
      v-if="breaks.isFetching && !breaks.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (initial only) -->
    <div
      v-else-if="breaks.error && !breaks.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="breaks.data">
      <!-- Metric Cards -->
      <div
        class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-6"
      >
        <IspMetricCard
          :label="t('ISP_REPORTS.PAUSAS.TOTAL_BREAKS')"
          :value="totalSessions"
          :loading="breaks.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.PAUSAS.TOTAL_DURATION')"
          :value="totalDuration"
          :loading="breaks.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.PAUSAS.AVG_DURATION')"
          :value="avgDuration"
          :loading="breaks.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.PAUSAS.LONGEST_BREAK')"
          :value="longestBreak"
          :loading="breaks.isFetching"
        />
      </div>

      <!-- Bar Chart -->
      <div class="mb-6">
        <IspBarChart
          v-if="barChartItems.length"
          :title="t('ISP_REPORTS.PAUSAS.BY_REASON')"
          :items="barChartItems"
        />
      </div>

      <!-- Table -->
      <IspDataTable
        v-if="tableRows.length"
        :title="t('ISP_REPORTS.PAUSAS.TABLE_TITLE')"
        :columns="tableColumns"
        :rows="tableRows"
      />
    </template>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center h-64 gap-2">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_PAUSAS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_PAUSAS_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ── Filter select wrapper ───────────────────────────────────────────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode) ────────────────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Export button: light mode ───────────────────────────────────────── */
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1.5px solid #d1d5db;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  color: #6b7280;
}
.btn-export:hover {
  border-color: #9ca3af;
  color: #374151;
}

/* ── DatePicker z-index fix ──────────────────────────────────────────── */
:deep(.date-picker) {
  z-index: 20;
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
/* ── Filter select: dark mode ────────────────────────────────────────── */
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
/* ── Export button: dark mode ────────────────────────────────────────── */
body.dark .btn-export {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
body.dark .btn-export:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}
</style>
